import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  ConfigProvider,
  DatePicker,
  Form,
  message,
  Modal,
  notification,
  Radio,
  Select,
  Spin,
} from "antd";
import {
  ArrowLeftOutlined,
  ExclamationCircleOutlined,
  LogoutOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import ModuleSelctorCards from "../../../../Components/Cards/ModuleSelectorCards";
import FuelIcon from "../../../../Assets/Icons/fuel-icon.svg";
import ClipboardIcon from "../../../../Assets/Icons/clipboard.svg";
import CarPartsIcon from "../../../../Assets/Icons/car_repair.svg";
import creditIcon from "../../../../Assets/Icons/credit_card_clock.svg";
import logo from "../../../../Assets/Images/logo.svg";
import logoutIcon from "../../../../Assets/Images/logoutIcon.svg";
import GenericModalForm from "../../../../Components/Modals/GenericModalForm";
import {
  getCurrentSubscription,
  createSubscriptionPayment,
  initiateWiPaySubscriptionPayment,
  resubmitSubscriptionPayment,
} from "../../../../API/FleetManager/SubscriptionPayments";
import { getModules } from "../../../../API/Config";
import { logout as logoutApi } from "../../../../API/auth";
import { UserContext } from "../../../../App";
import {
  getRefreshToken,
  handleLogout as clearUserSession,
  handleUpdateUser,
} from "../../../../Utlis/UpdateUserState";
import getFirstAllowedRoute from "../../../../Utlis/GetFirstAllowedRoute";
import { savePaymentReturn } from "../../../../Utlis/PaymentReturn";
import "../Subscription/style.css";
import "./style.css";

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL?.replace(
  /\/?api\/?$/,
  "",
);

const PAYMENT_SCREEN_ROUTE = "/my-subscription";

const PAGE_FONT = "Inter, sans-serif";

const PAYMENT_METHOD_OPTIONS = [
  { label: "Cash", value: "cash" },
  { label: "Bank Transfer", value: "bank_transfer" },
];

const PAYMENT_METHOD_LABELS = {
  cash: "Cash",
  bank_transfer: "Bank Transfer",

  wipay: "WiPay",
};

const WIPAY_RETURN_MESSAGES = {
  success: {
    type: "success",
    message: "Payment Successful",
    description: "Your WiPay payment was completed.",
  },
  failed: {
    type: "error",
    message: "Payment Failed",
    description: "Your WiPay payment was not completed. Please try again.",
  },
  error: {
    type: "error",
    message: "Payment Error",
    description: "Something went wrong with WiPay. Please try again.",
  },
};

const resolveAvatarUrl = (avatar) => {
  if (!avatar) return null;
  if (/^https?:\/\//i.test(avatar) || avatar.startsWith("blob:")) {
    return avatar;
  }
  return `${BACKEND_ORIGIN}${avatar.startsWith("/") ? "" : "/"}${avatar}`;
};

const getErrorMessage = (data, fallback) => {
  if (!data) return fallback;
  if (data.message) return data.message;
  if (data.detail) return data.detail;

  const fieldErrors = Object.entries(data)
    .filter(([key]) => key !== "data")
    .flatMap(([key, value]) =>
      []
        .concat(value)
        .filter((msg) => typeof msg === "string")
        .map((msg) =>
          key === "non_field_errors"
            ? msg
            : `${key.charAt(0).toUpperCase()}${key.slice(1).replace(/_/g, " ")}: ${msg}`,
        ),
    );

  return fieldErrors.length ? fieldErrors.join(" ") : fallback;
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const formatAmount = (amount, currency) => {
  const value = parseFloat(amount);
  if (Number.isNaN(value)) return "-";
  return `${currency || "JMD"} ${value.toFixed(2)}`;
};

const MODULE_ICONS_BY_CODE = {
  fuel: FuelIcon,
  maintenance: ClipboardIcon,
  parts: CarPartsIcon,
};

const buildModuleOption = (mod, isAnnual) => {
  const fallbackIcon =
    MODULE_ICONS_BY_CODE[mod.code?.toLowerCase()] || ClipboardIcon;
  const iconSrc = mod.icon ? resolveAvatarUrl(mod.icon) : fallbackIcon;
  const rawPrice = isAnnual
    ? mod.annual_price_per_vehicle
    : mod.monthly_price_per_vehicle;
  const price = parseFloat(rawPrice);
  const isInactive = mod.status ? mod.status !== "active" : false;

  return {
    id: mod.id,
    title: mod.name,
    description: mod.description,
    price: rawPrice
      ? price
        ? `${mod.currency || "JMD"} ${price.toFixed(2)}/vehicle`
        : "Free"
      : undefined,
    icon: <img src={iconSrc} alt={mod.name} />,
    disabled: isInactive,
    tag: isInactive ? "Coming Soon" : undefined,
  };
};

const MySubscription = ({ setUser }) => {
  const navigate = useNavigate();
  const { userData, setUserData, setUserToken } = useContext(UserContext);
  const [form] = Form.useForm();
  const watchedPaymentMethod = Form.useWatch("payment_method", form);
  const fileInputRef = useRef(null);
  const wipayHandledRef = useRef(false);

  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPayModalOpen, setIsPayModalOpen] = useState(false);
  const [isResubmit, setIsResubmit] = useState(false);
  const [proofFile, setProofFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [wipayLoading, setWipayLoading] = useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);

  // Renewal (expired subscription): the manager picks the modules and the
  // vehicle count; the backend prices it (renewal_quote) on every change.
  const [allModules, setAllModules] = useState([]);
  const [renewModuleIds, setRenewModuleIds] = useState([]);
  const [renewVehicles, setRenewVehicles] = useState(null);
  const [renewCycle, setRenewCycle] = useState(null);
  const [renewQuote, setRenewQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState(null);
  const [serverQuoteError, setServerQuoteError] = useState(null);
  const renewalInitRef = useRef(false);
  const renewRequestedRef = useRef(
    new URLSearchParams(window.location.search).get("renew") === "1",
  );
  const requestedCycleRef = useRef(
    ["monthly", "annual"].includes(
      new URLSearchParams(window.location.search).get("billing_cycle"),
    )
      ? new URLSearchParams(window.location.search).get("billing_cycle")
      : null,
  );

  const goToPortal = useCallback(() => {
    if (userData?.subscription_payment_required) {
      handleUpdateUser(setUserData, {
        ...userData,
        subscription_payment_required: false,
      });
    }
    navigate(
      getFirstAllowedRoute(
        "manager",
        userData?.page_access,
        "/dashboard-manager",
      ),
      { replace: true },
    );
  }, [navigate, setUserData, userData]);

  // "Back to Portal" only shows on an early renewal, which starts from the
  // Subscription page, so send the user back there.
  const backToSubscription = () => navigate("/subscription", { replace: true });

  const fetchSubscription = useCallback(async () => {
    setLoading(true);
    try {
      // Early renewal: ask for renewal_quote while still active.
      const res = await getCurrentSubscription(
        renewRequestedRef.current ? { is_renewal: "true" } : undefined,
      );
      const data = res?.data?.data;

      if (Math.floor(res?.status / 100) === 2 && data) {
        setSubscription(data);
        return data;
      }
      message.error(
        getErrorMessage(res?.data, "Failed to load current subscription."),
      );
    } catch (err) {
      if (
        err?.response?.status === 403 &&
        err?.response?.data?.code !== "subscription_payment_required"
      ) {
        return { subscription_payment_required: false };
      }
      message.error(
        getErrorMessage(
          err?.response?.data,
          "Failed to load current subscription.",
        ),
      );
    } finally {
      setLoading(false);
    }
    return null;
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const wipayStatus = params.get("wipay_status");

    if (wipayStatus && !wipayHandledRef.current) {
      wipayHandledRef.current = true;
      window.history.replaceState(null, "", PAYMENT_SCREEN_ROUTE);
    }

    fetchSubscription().then((data) => {
      if (!data) return;

      // Decide from the CURRENT state, not the first payment: an expired
      // subscription has initial_payment_status "paid" but is still locked
      // (sending it to the portal looped dashboard -> 403 -> back here).
      const isPaid = data.subscription_payment_required === false;

      if (wipayStatus) {
        const info = WIPAY_RETURN_MESSAGES[wipayStatus];
        if (info && !(wipayStatus === "success" && !isPaid)) {
          notification[info.type]({
            message: info.message,
            description: info.description,
            placement: "topRight",
          });
        }
      }

      // An active manager who came to renew early stays on this screen.
      if (isPaid && !(renewRequestedRef.current && !wipayStatus)) {
        goToPortal();
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // An expired subscription is a RENEWAL: its state comes from the open
  // renewal request (open_request), not the first payment. The first payment
  // keeps its sign-up modules / vehicle slots locked; a renewal lets the
  // manager pick from every module and enter the vehicle count, priced by the
  // backend (renewal_quote) -- the amount sent must match it.
  const isExpired = subscription?.subscription_status === "expired";
  // Early renewal while still active (from the banner) works the same way,
  // but sends is_renewal so the new cycle starts when the current one ends.
  const isEarlyRenewal =
    renewRequestedRef.current && subscription?.subscription_status === "active";
  const isRenewal = isExpired || isEarlyRenewal;
  const openRequest = subscription?.open_request;
  const renewalStatus = () => {
    if (!openRequest || openRequest.can_create_request)
      return "awaiting_payment";
    return openRequest.status === "revision_requested"
      ? "revision_requested"
      : "under_review";
  };
  const status = isRenewal
    ? renewalStatus()
    : subscription?.initial_payment_status;
  const paymentRequest = isRenewal
    ? openRequest?.request
    : subscription?.initial_payment_request;
  const canPay = status === "awaiting_payment" || status === "rejected";
  // Modules / vehicle count are editable only while choosing a new renewal.
  const canEditRenewal = isRenewal && canPay;
  const vehiclesInUse = subscription?.vehicles_in_use || 0;
  const minVehicles = Math.max(vehiclesInUse, 1);

  // Load every module once a renewal is needed.
  useEffect(() => {
    if (!isRenewal) return;
    getModules().then((res) => {
      const list = res?.data?.data;
      if (res?.status === 200 && Array.isArray(list)) {
        setAllModules(list);
      } else {
        message.error(getErrorMessage(res?.data, "Failed to load modules."));
      }
    });
  }, [isRenewal]);

  // Prefill the renewal choices from the current plan (once).
  useEffect(() => {
    if (!isRenewal || renewalInitRef.current) return;
    const quote = subscription?.renewal_quote;
    renewalInitRef.current = true;
    setRenewModuleIds(
      quote?.module_ids ||
        (subscription?.assigned_modules || []).map((mod) => mod.id),
    );
    setRenewVehicles(
      quote?.vehicle_count ?? subscription?.vehicle_limit ?? null,
    );
    setRenewCycle(
      requestedCycleRef.current ||
        quote?.billing_cycle ||
        subscription?.billing_cycle ||
        "monthly",
    );
    setRenewQuote(quote || null);
  }, [isRenewal, subscription]);

  // Re-price the renewal whenever the manager changes the choices.
  useEffect(() => {
    if (!canEditRenewal || !renewalInitRef.current) return undefined;
    if (!renewModuleIds.length) {
      setQuoteLoading(false);
      setRenewQuote(null);
      setQuoteError("Select at least one module.");
      setServerQuoteError(null);
      return undefined;
    }
    if (!renewVehicles || renewVehicles < minVehicles) {
      setQuoteLoading(false);
      setRenewQuote(null);
      setQuoteError(
        `Enter at least ${minVehicles} vehicle${minVehicles === 1 ? "" : "s"}` +
          (vehiclesInUse ? ` (you have ${vehiclesInUse} vehicles).` : "."),
      );
      setServerQuoteError(null);
      return undefined;
    }
    let cancelled = false;
    setQuoteLoading(true);
    const timer = setTimeout(async () => {
      try {
        const res = await getCurrentSubscription({
          module_ids: renewModuleIds.join(","),
          billing_cycle: renewCycle,
          vehicle_count: renewVehicles,
          ...(isEarlyRenewal ? { is_renewal: "true" } : {}),
        });
        if (cancelled) return;
        setRenewQuote(res?.data?.data?.renewal_quote || null);
        setQuoteError(null);
        setServerQuoteError(null);
      } catch (err) {
        if (cancelled) return;
        const errorText = getErrorMessage(
          err?.response?.data,
          "Failed to calculate the amount.",
        );
        setRenewQuote(null);
        setQuoteError(errorText);
        setServerQuoteError(errorText);
      } finally {
        if (!cancelled) setQuoteLoading(false);
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    canEditRenewal,
    isEarlyRenewal,
    renewModuleIds,
    renewCycle,
    renewVehicles,
    minVehicles,
    vehiclesInUse,
  ]);

  const payAmount = isRenewal
    ? canEditRenewal
      ? renewQuote?.amount
      : subscription?.renewal_quote?.amount
    : subscription?.amount;
  // The renewal's own billing period (picked by the manager) drives the
  // prices shown; otherwise the company's current cycle.
  const isAnnual =
    (canEditRenewal ? renewCycle : subscription?.billing_cycle) === "annual";
  const assignedModules = subscription?.assigned_modules || [];
  const moduleOptions = (canEditRenewal ? allModules : assignedModules).map(
    (mod) => buildModuleOption(mod, isAnnual),
  );
  const selectedModuleIds = canEditRenewal
    ? renewModuleIds
    : assignedModules.map((mod) => mod.id);
  const hasAmount = payAmount !== null && payAmount !== undefined;
  const renewalNotReady = canEditRenewal && (quoteLoading || !!quoteError);

  // The renewal choices sent with the payment (manual / WiPay).
  const renewalPayload = () =>
    canEditRenewal
      ? {
          module_ids: renewModuleIds,
          billing_cycle: renewCycle,
          vehicle_count: renewVehicles,
          amount: payAmount,
          ...(isEarlyRenewal ? { is_renewal: true } : {}),
        }
      : isRenewal && hasAmount
        ? { amount: payAmount }
        : {};

  const handleUploadClick = () => fileInputRef.current?.click();

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProofFile(file);
      form.setFields([{ name: "proof_file", errors: [] }]);
    }
    e.target.value = null;
  };

  const openManualPayment = (resubmit = false) => {
    setIsResubmit(resubmit);
    form.resetFields();
    setProofFile(null);
    if (resubmit && paymentRequest) {
      form.setFieldsValue({
        payment_method: paymentRequest.payment_method || undefined,
      });
    }
    setIsPayModalOpen(true);
  };

  const closeManualPayment = () => {
    setIsPayModalOpen(false);
    form.resetFields();
    setProofFile(null);
  };

  const handleSubmitPayment = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (err) {
      return;
    }

    const formData = new FormData();
    if (values.payment_method) {
      formData.append("payment_method", values.payment_method);
    }
    if (values.payment_date) {
      formData.append("payment_date", values.payment_date.format("YYYY-MM-DD"));
    }
    if (proofFile) formData.append("proof_file", proofFile);
    // Renewal: chosen modules (repeated field), vehicle count and amount.
    // A revision resubmit (not editable here) sends only the amount.
    const renewal = renewalPayload();
    (renewal.module_ids || []).forEach((id) =>
      formData.append("module_ids", id),
    );
    if (renewal.billing_cycle)
      formData.append("billing_cycle", renewal.billing_cycle);
    if (renewal.vehicle_count)
      formData.append("vehicle_count", renewal.vehicle_count);
    if (renewal.amount) formData.append("amount", renewal.amount);
    if (renewal.is_renewal) formData.append("is_renewal", "true");

    setSubmitting(true);
    try {
      const res = isResubmit
        ? await resubmitSubscriptionPayment(paymentRequest?.id, formData)
        : await createSubscriptionPayment(formData);

      if (Math.floor(res?.status / 100) === 2) {
        notification.success({
          message: isResubmit ? "Payment Resubmitted" : "Payment Submitted",
          description:
            res?.data?.message ||
            "Your payment has been submitted for admin approval.",
          placement: "topRight",
        });
        closeManualPayment();
        fetchSubscription();
      } else {
        message.error(getErrorMessage(res?.data, "Failed to submit payment."));
      }
    } catch (err) {
      message.error(
        getErrorMessage(err?.response?.data, "Failed to submit payment."),
      );
      if (err?.response?.status === 400 && err?.response?.data?.message) {
        closeManualPayment();
        fetchSubscription();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleWiPayPayment = async () => {
    setWipayLoading(true);
    try {
      const res = await initiateWiPaySubscriptionPayment({
        success_url: `${window.location.origin}/payment/success`,
        failure_url: `${window.location.origin}/payment/failed`,
        ...renewalPayload(),
      });
      const checkoutUrl = res?.data?.data?.checkout_url;

      if (Math.floor(res?.status / 100) === 2 && checkoutUrl) {
        savePaymentReturn({ pathname: PAYMENT_SCREEN_ROUTE });
        window.location.href = checkoutUrl;
        return;
      }

      notification.error({
        message: "WiPay Payment Failed",
        description: getErrorMessage(
          res?.data,
          "WiPay checkout URL was not returned.",
        ),
        placement: "topRight",
      });
    } catch (err) {
      notification.error({
        message: "WiPay Payment Failed",
        description: getErrorMessage(
          err?.response?.data,
          err?.response?.status === 502
            ? "Payment service unavailable, try again."
            : "Failed to initiate WiPay payment.",
        ),
        placement: "topRight",
      });
    }
    setWipayLoading(false);
  };

  const handleLogout = async () => {
    try {
      await logoutApi(getRefreshToken());
    } catch (err) {
      console.log(err);
    }
    clearUserSession(setUserData, setUserToken);
    setIsLogoutModalOpen(false);
    setUser?.("public_manager");
    navigate("/selection", { replace: true });
  };

  const renderSubmittedDetails = () => (
    <div className="my-subscription__details">
      <div>
        <span className="my-subscription__details-label">Amount</span>
        <span>
          {formatAmount(paymentRequest?.amount, subscription?.currency)}
        </span>
      </div>
      <div>
        <span className="my-subscription__details-label">Payment Method</span>
        <span>
          {PAYMENT_METHOD_LABELS[paymentRequest?.payment_method] ||
            paymentRequest?.payment_method ||
            "-"}
        </span>
      </div>
      <div>
        <span className="my-subscription__details-label">Submitted At</span>
        <span>{formatDateTime(paymentRequest?.submitted_at)}</span>
      </div>
    </div>
  );

  const renderStatusBanner = () => {
    if (status === "under_review") {
      return (
        <Alert
          type="info"
          showIcon
          message="Payment submitted, waiting for approval"
          description={renderSubmittedDetails()}
        />
      );
    }

    if (status === "revision_requested") {
      return (
        <Alert
          type="warning"
          showIcon
          message="Your payment was sent back for revision"
          description={
            <>
              <p className="my-subscription__notes">
                {paymentRequest?.audit_notes || "No notes provided."}
              </p>
              {renderSubmittedDetails()}
            </>
          }
        />
      );
    }

    if (status === "rejected") {
      return (
        <Alert
          type="error"
          showIcon
          message="Your previous payment was rejected"
          description={
            <p className="my-subscription__notes">
              {paymentRequest?.audit_notes || "No notes provided."}
            </p>
          }
        />
      );
    }

    return null;
  };

  return (
    <ConfigProvider theme={{ token: { fontFamily: PAGE_FONT } }}>
      <div className="my-subscription">
        <div className="my-subscription__top">
          <img src={logo} alt="logo" />
          {isEarlyRenewal ? (
            // Still active: this is optional, so let them go back.
            <Button icon={<ArrowLeftOutlined />} onClick={backToSubscription}>
              Back to Portal
            </Button>
          ) : (
            <Button
              icon={<LogoutOutlined />}
              onClick={() => setIsLogoutModalOpen(true)}
            >
              Logout
            </Button>
          )}
        </div>

        {canEditRenewal && serverQuoteError && !quoteLoading && (
          <div className="my-subscription__lock" role="alertdialog">
            <div className="my-subscription__lock-card">
              <div className="my-subscription__lock-icon">
                <ExclamationCircleOutlined />
              </div>
              <div className="my-subscription__lock-title">
                We couldn't price this renewal
              </div>
              <div className="my-subscription__lock-message">
                {serverQuoteError}
              </div>
              <div className="my-subscription__lock-actions">
                {isEarlyRenewal ? (
                  <Button
                    type="primary"
                    icon={<ArrowLeftOutlined />}
                    onClick={backToSubscription}
                  >
                    Back to Portal
                  </Button>
                ) : (
                  <Button
                    type="primary"
                    icon={<LogoutOutlined />}
                    onClick={() => setIsLogoutModalOpen(true)}
                  >
                    Logout
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="subscription-page my-subscription__content">
          {loading && !subscription ? (
            <div className="my-subscription__loading">
              <Spin size="large" />
            </div>
          ) : (
            <>
              {canEditRenewal &&
                quoteError &&
                !serverQuoteError &&
                !quoteLoading && (
                <Alert
                  type="warning"
                  showIcon
                  message={quoteError}
                  className="my-subscription__quote-error"
                />
              )}

              {renderStatusBanner()}

              <ModuleSelctorCards
                options={moduleOptions}
                headerExtra={
                  canEditRenewal ? (
                    // Renewal: the manager picks the billing period.
                    <Radio.Group
                      optionType="button"
                      buttonStyle="solid"
                      className="custom-segmented-control"
                      value={renewCycle}
                      onChange={(e) => setRenewCycle(e.target.value)}
                      options={[
                        { label: "Monthly", value: "monthly" },
                        { label: "Annual", value: "annual" },
                      ]}
                    />
                  ) : (
                    <span className="my-subscription__cycle">
                      {isAnnual ? "Annual" : "Monthly"} Billing
                    </span>
                  )
                }
                headerSubtitle={
                  canPay && (
                    <p className="module-subtitle">
                      {isEarlyRenewal
                        ? "Renew your subscription now. The new period starts when the current one ends, so no days are lost."
                        : isExpired
                          ? "Your subscription has expired. Renew it to access the portal."
                          : "Your request has been approved by the admin. Please complete the payment to access the portal."}
                    </p>
                  )
                }
                // First payment: locked to the sign-up selection. Renewal:
                // every module is selectable and the vehicle count editable.
                title={canEditRenewal ? "Select Modules" : "Selected Module"}
                allowSelection={canEditRenewal}
                onChange={canEditRenewal ? setRenewModuleIds : undefined}
                footerLabel={isAnnual ? "Annual Rate" : "Monthly Rate"}
                showFooter={true}
                selectedIds={selectedModuleIds}
                multiple={true}
                showVehicleInput={true}
                vehicleLabel={
                  canEditRenewal ? "Number of Vehicles *" : undefined
                }
                vehicleCount={
                  canEditRenewal
                    ? renewVehicles
                    : subscription?.vehicle_limit || 0
                }
                onVehicleChange={canEditRenewal ? setRenewVehicles : undefined}
                vehicleMin={canEditRenewal ? minVehicles : 1}
                totalText={
                  canEditRenewal
                    ? quoteLoading
                      ? "Calculating..."
                      : formatAmount(payAmount, subscription?.currency)
                    : undefined
                }
              />

              {canPay && !hasAmount && !canEditRenewal && (
                <Alert
                  type="warning"
                  showIcon
                  message="No modules or vehicle slots on your subscription. Contact the administrator."
                />
              )}

              {(canPay || status === "revision_requested") && hasAmount && (
                <div className="my-subscription__summary">
                  <div>
                    <div className="my-subscription__total-label">
                      Total Amount
                    </div>
                    <div className="my-subscription__total-value">
                      {formatAmount(payAmount, subscription.currency)}
                    </div>
                  </div>

                  <div className="my-subscription__actions">
                    {canPay ? (
                      <>
                        <Button
                          onClick={handleWiPayPayment}
                          loading={wipayLoading}
                          disabled={renewalNotReady}
                        >
                          Pay with WiPay
                        </Button>
                        <Button
                          type="primary"
                          onClick={() => openManualPayment(false)}
                          disabled={wipayLoading || renewalNotReady}
                        >
                          Pay Manually
                        </Button>
                      </>
                    ) : (
                      <Button
                        type="primary"
                        onClick={() => openManualPayment(true)}
                      >
                        Resubmit Payment
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <Modal
          className="generic-modal"
          open={isPayModalOpen}
          onCancel={closeManualPayment}
          title={null}
          footer={null}
          centered
          closable={!submitting}
          maskClosable={!submitting}
        >
          <div className="top-gradient"></div>

          <div className="modal-body-wrapper">
            <div className="modal-header">
              <div
                className="modal-icon-wrapper"
                style={{ backgroundColor: "#f3f4f6" }}
              >
                <img src={creditIcon} alt="pay" />
              </div>
              <span className="modal-title">
                {isResubmit
                  ? "Resubmit Payment"
                  : "Pay to Activate Subscription"}
              </span>
            </div>

            <Form form={form} layout="vertical" className="modal-form">
              <Form.Item
                name="payment_method"
                label="Payment Method"
                required={false}
                rules={
                  isResubmit
                    ? []
                    : [
                        {
                          required: true,
                          message: "Payment method is required",
                        },
                      ]
                }
              >
                <Select
                  placeholder="Select payment method"
                  options={PAYMENT_METHOD_OPTIONS}
                />
              </Form.Item>

              <Form.Item
                name="payment_date"
                label="Payment Date"
                required={false}
                rules={
                  isResubmit
                    ? []
                    : [{ required: true, message: "Payment date is required" }]
                }
              >
                <DatePicker
                  style={{ width: "100%" }}
                  format="YYYY-MM-DD"
                  disabled={!watchedPaymentMethod}
                  placeholder={
                    watchedPaymentMethod
                      ? "Select date"
                      : "Select payment method first"
                  }
                />
              </Form.Item>

              <Form.Item
                name="proof_file"
                label="Proof of Payment"
                required={false}
                rules={
                  isResubmit
                    ? []
                    : [
                        {
                          validator: () =>
                            proofFile
                              ? Promise.resolve()
                              : Promise.reject(
                                  new Error("Proof of payment is required"),
                                ),
                        },
                      ]
                }
              >
                <div>
                  <Button
                    icon={<UploadOutlined />}
                    onClick={handleUploadClick}
                    className="proof-upload-btn"
                    title={proofFile ? proofFile.name : undefined}
                  >
                    <span className="proof-upload-btn__name">
                      {proofFile ? proofFile.name : "Upload Proof File"}
                    </span>
                  </Button>
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                    accept="image/*,.pdf"
                  />
                </div>
              </Form.Item>
            </Form>

            <div className="modal-footer-wrapper subscription-pay-footer">
              <Button onClick={closeManualPayment} disabled={submitting}>
                Cancel
              </Button>
              <Button
                type="primary"
                onClick={handleSubmitPayment}
                loading={submitting}
              >
                {isResubmit ? "Resubmit Payment" : "Submit Payment"}
              </Button>
            </div>
          </div>
        </Modal>

        <GenericModalForm
          open={isLogoutModalOpen}
          icon={logoutIcon}
          iconBg="#E9E7FB"
          title="Log out?"
          subtitle="Are you sure you want to log out?"
          okText="Sure"
          cancelText="Cancel"
          onCancel={() => setIsLogoutModalOpen(false)}
          onOk={handleLogout}
        />
      </div>
    </ConfigProvider>
  );
};

export default MySubscription;
